import {
  Workflow,
  WorkflowDetails,
  WorkflowRun,
  WorkflowTrigger,
} from '@qtek/shared/models';

type WorkflowRow = Partial<Workflow> & { _id: string };
type WorkflowRowWithTriggers = Workflow & { triggers?: WorkflowTrigger[] };

export function upsertWorkflowRow(
  byItem: Record<string, Workflow[]>,
  itemId: string,
  row: WorkflowRow,
  op: 'ins' | 'upd' | 'query'
): Record<string, Workflow[]> {
  const list = byItem[itemId] ?? [];
  const index = list.findIndex(w => w._id === row._id);
  if (index >= 0) {
    const next = list.slice();
    next[index] = { ...list[index], ...row };
    return { ...byItem, [itemId]: next };
  }
  // Trimmed upd broadcasts may omit `item`; only full rows can join a list.
  if (op === 'upd' || row.item !== itemId || row.parentWorkflow) return byItem;
  return { ...byItem, [itemId]: [...list, row as Workflow] };
}

export function removeWorkflowRow(
  byItem: Record<string, Workflow[]>,
  id: string
): Record<string, Workflow[]> {
  return Object.fromEntries(
    Object.entries(byItem).map(([item, list]) => [
      item,
      list.filter(w => w._id !== id),
    ])
  );
}

/**
 * Mirrors a trigger change into the list row of its workflow, so the list's
 * trigger column stays current without a reload. Rows that are not loaded, and
 * trimmed `upd` pushes for triggers the row does not hold, are left untouched.
 */
export function upsertWorkflowRowTrigger(
  byItem: Record<string, Workflow[]>,
  workflowId: string,
  trigger: Partial<WorkflowTrigger> & { _id: string },
  op: 'ins' | 'upd' | 'del'
): Record<string, Workflow[]> {
  let changed = false;
  const next = Object.fromEntries(
    Object.entries(byItem).map(([item, list]) => [
      item,
      list.map(row => {
        if (row._id !== workflowId) return row;
        const triggers = (row as WorkflowRowWithTriggers).triggers ?? [];
        const known = triggers.some(t => t._id === trigger._id);
        if (op === 'upd' && !known) return row;
        changed = true;
        const updated =
          op === 'del'
            ? triggers.filter(t => t._id !== trigger._id)
            : known
              ? triggers.map(t =>
                  t._id === trigger._id ? { ...t, ...trigger } : t
                )
              : [...triggers, trigger as WorkflowTrigger];
        return { ...row, triggers: updated };
      }),
    ])
  );
  return changed ? next : byItem;
}

export function upsertWorkflowRun(
  runs: WorkflowRun[],
  run: WorkflowRun
): WorkflowRun[] {
  const exists = runs.some(r => r._id === run._id);
  return exists
    ? runs.map(r => (r._id === run._id ? { ...r, ...run } : r))
    : [...runs, run];
}

export function mergeActiveWorkflow(
  active: WorkflowDetails | null,
  row: WorkflowRow
): WorkflowDetails | null {
  return active && active._id === row._id ? { ...active, ...row } : active;
}
